import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import 'tailwindcss/tailwind.css';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useEffect, useState, useCallback } from 'react';
import { API_BASE_URLS } from '../constants/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const heartRateOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: false,
    },
    tooltip: { enabled: false },
    datalabels: {
      display: true,
      align: 'top' as const,
      anchor: 'end' as const,
      color: '#232B3A',
      font: { weight: 'normal' as const, size: 12 },
      formatter: function(value: number) { return value; },
    },
  },
  scales: {
    x: { 
      grid: { 
        display: true, 
        color: '#e5e7eb',
        borderDash: [10, 10],
        drawBorder: false
      },
      display: true,
      ticks: {
        color: '#6B7280',
        font: { size: 10 }
      }
    },
    y: { 
      grid: { 
        color: '#e5e7eb',
        borderDash: [10, 10],
        drawBorder: false
      }, 
      min: 0, 
      max: 200, 
      ticks: { 
        stepSize: 50,
        display: false
      }, 
      display: true 
    },
  },
};

const bloodPressureOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: false,
    },
    tooltip: { enabled: false },
    datalabels: {
      display: true,
      align: 'top' as const,
      anchor: 'end' as const,
      color: '#232B3A',
      font: { weight: 'normal' as const, size: 12 },
      formatter: function(value: any) { 
        if (Array.isArray(value)) {
          return `${value[1]}/${value[0]}`;
        }
        return value;
      },
    },
  },
  scales: {
    x: { 
      grid: { 
        display: true, 
        color: '#e5e7eb',
        borderDash: [10, 10],
        drawBorder: false
      },
      display: true,
      ticks: {
        color: '#6B7280',
        font: { size: 10 }
      }
    },
    y: { 
      grid: { 
        color: '#e5e7eb',
        borderDash: [10, 10],
        drawBorder: false
      }, 
      min: 0, 
      max: 200, 
      ticks: { 
        stepSize: 50,
        display: false
      }, 
      display: true 
    },
  },
  elements: {
    bar: {
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    },
  },
};

const bloodOxygenOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: false,
    },
    tooltip: { enabled: false },
    datalabels: {
      display: true,
      align: 'top' as const,
      anchor: 'end' as const,
      color: '#232B3A',
      font: { weight: 'normal' as const, size: 12 },
      formatter: function(value: number) { return `${value}%`; },
    },
  },
  scales: {
    x: { 
      grid: { 
        display: true, 
        color: '#e5e7eb',
        borderDash: [10, 10],
        drawBorder: false
      },
      display: true,
      ticks: {
        color: '#6B7280',
        font: { size: 10 }
      }
    },
    y: {
      grid: { 
        color: '#e5e7eb',
        borderDash: [10, 10],
        drawBorder: false
      },
      min: 70,
      max: 105,
      ticks: {
        stepSize:10,
        callback: (value: number | string) => `${value}%`,
        display: false
      },
      display: true,
    },
  },
  elements: {
    bar: {
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    },
  },
};

interface HealthMetricsDashboardProps {
  vitals: any;
  patientId: string;
  heartRateDataForChart: number[];
  setHeartRateDataForChart: (data: number[]) => void;
  bloodPressureDataForChart: [number, number][];
  setBloodPressureDataForChart: (data: [number, number][]) => void;
  bloodOxygenDataForChart: number[];
  setBloodOxygenDataForChart: (data: number[]) => void;
  latestHeartRate: number;
  setLatestHeartRate: (value: number) => void;
  latestBloodPressure: { systolic: number; diastolic: number };
  setLatestBloodPressure: (value: { systolic: number; diastolic: number }) => void;
  latestBloodOxygen: number;
  setLatestBloodOxygen: (value: number) => void;
  latestHeartRateTime: string;
  setLatestHeartRateTime: (value: string) => void;
  latestBloodPressureTime: string;
  setLatestBloodPressureTime: (value: string) => void;
  latestBloodOxygenTime: string;
  setLatestBloodOxygenTime: (value: string) => void;
}

export default function HealthMetricsDashboard({ 
  patientId,
  heartRateDataForChart,
  setHeartRateDataForChart,
  bloodPressureDataForChart,
  setBloodPressureDataForChart,
  bloodOxygenDataForChart,
  setBloodOxygenDataForChart,
  latestHeartRate,
  setLatestHeartRate,
  latestBloodPressure,
  setLatestBloodPressure,
  latestBloodOxygen,
  setLatestBloodOxygen,
  latestHeartRateTime,
  setLatestHeartRateTime,
  latestBloodPressureTime,
  setLatestBloodPressureTime,
  latestBloodOxygenTime,
  setLatestBloodOxygenTime
}: HealthMetricsDashboardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialData, setHasInitialData] = useState(false);

  // Helper function to format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  // Fetch patient data
  const fetchPatientData = useCallback(async () => {
    try {
      // Only show loading on initial load, not on subsequent requests
      if (!hasInitialData) {
        setIsLoading(true);
      }
      const response = await fetch(`${API_BASE_URLS.HELIUS_SERVICE}/patients/${patientId}`);
      if (response.ok) {
        const data = await response.json();
        const vitals = data.vitals;
        
        // Helper functions to process vitals data
        const getLatestVitalFromData = (bodyType: string) => {
          if (!vitals) return undefined;
          let all: any[] = [];
          Object.values(vitals).forEach((arr: any) => {
            if (Array.isArray(arr)) {
              all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
            }
          });
          if (all.length === 0) return undefined;
          all.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          return all[0];
        };

        const getLatestVitalsForChartFromData = (bodyType: string) => {
          if (!vitals) return [];
          let all: any[] = [];
          Object.values(vitals).forEach((arr: any) => {
            if (Array.isArray(arr)) {
              all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
            }
          });
          if (all.length === 0) return [];
          all.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          return all.slice(0, 6); // Get latest 5 records
        };
        
        // Update latest values
        const bpVital = getLatestVitalFromData('BLOOD_PRESSURE');
        const hrVital = getLatestVitalFromData('HEART_RATE');
        const boVital = getLatestVitalFromData('BLOOD_OXYGEN');
        
        if (bpVital && bpVital.body && bpVital.body.systolic && bpVital.body.diastolic) {
          setLatestBloodPressure({
            systolic: bpVital.body.systolic,
            diastolic: bpVital.body.diastolic
          });
        }
        
        if (hrVital && hrVital.body && hrVital.body.value) {
          setLatestHeartRate(hrVital.body.value);
        }
        
        if (boVital && boVital.body && boVital.body.value) {
          setLatestBloodOxygen(boVital.body.value);
        }

        // Update chart data
        const heartRateVitals = getLatestVitalsForChartFromData('HEART_RATE');
        const bloodPressureVitals = getLatestVitalsForChartFromData('BLOOD_PRESSURE');
        const bloodOxygenVitals = getLatestVitalsForChartFromData('BLOOD_OXYGEN');

        // Map heart rate data
        const heartRateData = heartRateVitals.map(vital => vital.body.value).reverse();
        setHeartRateDataForChart(heartRateData);

        // Map blood pressure data
        const bloodPressureData = bloodPressureVitals.map(vital => [vital.body.diastolic, vital.body.systolic] as [number, number]).reverse();
        setBloodPressureDataForChart(bloodPressureData);

        // Map blood oxygen data
        const bloodOxygenData = bloodOxygenVitals.map(vital => vital.body.value).reverse();
        setBloodOxygenDataForChart(bloodOxygenData);
        
        // Set the latest reading times to the last time label from the chart
        // We'll set these after the component renders with the timeLabels
        
        // Mark that we have initial data
        if (!hasInitialData) {
          setHasInitialData(true);
        }
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
    } finally {
      if (!hasInitialData) {
        setIsLoading(false);
      }
    }
  }, [patientId, hasInitialData]);

  // Set up interval to fetch data every 2 minutes
  useEffect(() => {
    fetchPatientData(); // Initial fetch
    
    const interval = setInterval(() => {
      fetchPatientData();
    },2 * 60 * 1000); // 2 minutes for testing

    return () => clearInterval(interval);
  }, [patientId, fetchPatientData]);

  const bloodPressure = `${latestBloodPressure.systolic}/${latestBloodPressure.diastolic}`;
  const heartRate = latestHeartRate;
  const bloodOxygen = latestBloodOxygen;
  
  // Check if data is available
  const hasHeartRateData = heartRateDataForChart.length > 0;
  const hasBloodPressureData = bloodPressureDataForChart.length > 0;
  const hasBloodOxygenData = bloodOxygenDataForChart.length > 0;
  // Generate dynamic time labels for the past hour with 15-minute intervals
  const generateTimeLabels = () => {
    const now = new Date();
    const labels = [];
    
    // Round current time down to nearest 15-minute interval
    const currentMinutes = now.getMinutes();
    const roundedMinutes = Math.floor(currentMinutes / 15) * 15;
    const roundedNow = new Date(now);
    roundedNow.setMinutes(roundedMinutes, 0, 0);
    
    // Generate 6 labels going back from rounded current time
    for (let i = 5; i >= 0; i--) {
      const time = new Date(roundedNow.getTime() - (i * 15 * 60 * 1000));
      const hours = time.getHours();
      const minutes = time.getMinutes();
             const displayHours = hours % 12 || 12;
       const displayMinutes = `:${minutes.toString().padStart(2, '0')}`;
       
       // Only include AM/PM for the first label (i = 5) and make it lowercase
       if (i === 5) {
         const ampm = hours >= 12 ? 'pm' : 'am';
         labels.push(`${displayHours}${displayMinutes} ${ampm}`);
       } else {
         labels.push(`${displayHours}${displayMinutes}`);
       }
    }
    
    return labels;
  };

  const timeLabels = generateTimeLabels();

  // Function to add AM/PM to time if not present
  const addAmPmToTime = (timeString: string) => {
    // If already has AM/PM, return as is
    if (timeString.includes('am') || timeString.includes('pm')) {
      return timeString;
    }
    
    // For the last time label, we need to determine AM/PM based on the actual current time
    // since the chart labels are generated from current time going back
    const now = new Date();
    const currentMinutes = now.getMinutes();
    const roundedMinutes = Math.floor(currentMinutes / 15) * 15;
    const roundedNow = new Date(now);
    roundedNow.setMinutes(roundedMinutes, 0, 0);
    
    // The last label corresponds to the current rounded time
    const hours = roundedNow.getHours();
    const minutes = roundedNow.getMinutes();
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  // Set the latest reading times to the last time label from the chart with AM/PM
  useEffect(() => {
    if (timeLabels.length > 0) {
      const lastTimeLabel = timeLabels[timeLabels.length - 1];
      const timeWithAmPm = addAmPmToTime(lastTimeLabel);
      setLatestHeartRateTime(timeWithAmPm);
      setLatestBloodPressureTime(timeWithAmPm);
      setLatestBloodOxygenTime(timeWithAmPm);
    }
  }, [timeLabels, setLatestHeartRateTime, setLatestBloodPressureTime, setLatestBloodOxygenTime]);

  // Create chart data with latest values
  const heartRateData = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Heart Rate',
        data: heartRateDataForChart, // Use latest heart rate as last value
        borderColor: '#F43F5E', // pink/red
        backgroundColor: 'rgba(244,63,94,0.1)',
        tension: 0.1,
        fill: false,
        pointRadius: 4,
        pointBackgroundColor: '#FFFFFF',
        pointBorderColor: '#F43F5E',
        pointBorderWidth: 2,
        datalabels: {
          align: 'top' as const,
          anchor: 'end' as const,
          color: '#232B3A',
          font: { weight: 'normal' as const, size: 12 },
          formatter: function(value: number) { return value; },
          display: true
        },
      },
    ],
  };

  const bloodPressureData = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Blood Pressure',
        data: bloodPressureDataForChart,
        backgroundColor: bloodPressureDataForChart.map(([diastolic, systolic]) => 
          systolic >= 140 || diastolic >= 90 ? '#FB7185' : 
          systolic >= 120 || diastolic >= 80 ? '#FB7185' : '#2DD4BF'
        ),
        borderColor: bloodPressureDataForChart.map(([diastolic, systolic]) => 
          systolic >= 140 || diastolic >= 90 ? '#FB7185' : 
          systolic >= 120 || diastolic >= 80 ? '#FB7185' : '#2DD4BF'
        ),
        borderWidth: 1,
        barThickness: 50,
        maxBarThickness: 50,
      },
    ],
  };

  const bloodOxygenData = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Blood Oxygen',
        data: bloodOxygenDataForChart, // Use latest blood oxygen as last value
        backgroundColor: bloodOxygenDataForChart.map(value => 
          value < 90 ? '#FB7185' : value < 95 ? '#FB7185' : '#2DD4BF'
        ),
        borderColor: bloodOxygenDataForChart.map(value => 
          value < 90 ? '#FB7185' : value < 95 ? '#FB7185' : '#2DD4BF'
        ),
        borderWidth: 1,
        barThickness: 50,
        maxBarThickness: 50,
      },
    ],
  };

  // Threshold functions from DataTable
  const getHeartRateColor = (heartRate: number) => {
    if (heartRate < 60 || heartRate > 100) {
      return "text-rag-red" // High risk
    } else if (heartRate < 70 || heartRate > 90) {
      return "text-rag-amber" // Medium risk
    } else {
      return "text-rag-green" // Low risk
    }
  }

  const getBloodPressureColor = (systolic: number, diastolic: number) => {
    if (systolic >= 140 || diastolic >= 90) {
      return "text-rag-red" // High risk
    } else if (systolic >= 120 || diastolic >= 80) {
      return "text-rag-amber" // Medium risk
    } else {
      return "text-rag-green" // Low risk
    }
  }

  const getBloodOxygenColor = (bloodOxygen: number) => {
    if (bloodOxygen < 90) {
      return "text-rag-red" // High risk
    } else if (bloodOxygen < 95) {
      return "text-rag-amber" // Medium risk
    } else {
      return "text-rag-green" // Low risk
    }
  }

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading health metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 mb-6 overflow-hidden">
      {/* Heart Rate Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5 overflow-hidden">
        <div className="flex items-center justify-between w-full">
          {/* Left Panel - Icon and Current Value */}
          <div className="flex flex-col items-start mb-20">
            <div className="flex items-center gap-2 mb-3">
              <img src="/images/heart_rate.png" alt="Heart Rate" className="h-6 w-6" />
              <span className="text-lg font-bold text-gray-800">Heart Rate</span>
            </div>
            {hasHeartRateData && (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className={`text-3xl font-bold ${getHeartRateColor(heartRate)}`}>{heartRate}</span>
                  <span className="text-sm text-gray-500 font-semibold">BPM</span>
                  {(heartRate < 60 || heartRate > 100) && (
                    <svg className="h-5 w-5 text-rag-red" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                    </svg>
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {latestHeartRateTime}
                </div>
              </div>
            )}
          </div>
          
          {/* Right Panel - Line Chart */}
          <div className="flex-1 h-48 ml-6">
            {hasHeartRateData ? (
              <Line data={heartRateData} options={heartRateOptions} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <span className="text-gray-500 text-sm">No data available</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blood Pressure Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5 overflow-hidden">
        <div className="flex items-center justify-between w-full">
          {/* Left Panel - Icon and Current Value */}
          <div className="flex flex-col items-start mb-20">
            <div className="flex items-center gap-2 mb-3">
              <img src="/images/blood_pressure.png" alt="Blood Pressure" className="h-6 w-6" />
              <span className="text-lg font-bold text-gray-800">Blood Pressure</span>
            </div>
            {hasBloodPressureData && (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className={`text-3xl font-bold ${getBloodPressureColor(latestBloodPressure.systolic, latestBloodPressure.diastolic)}`}>{bloodPressure}</span>
                  <span className="text-sm text-gray-500 font-semibold">mmHg</span>
                  {(latestBloodPressure.systolic >= 140 || latestBloodPressure.diastolic >= 90) && (
                    <svg className="h-5 w-5 text-rag-red" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                    </svg>
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {latestBloodPressureTime}
                </div>
              </div>
            )}
          </div>
          
          {/* Right Panel - Bar Chart */}
          <div className="flex-1 h-48 ml-6">
            {hasBloodPressureData ? (
              <Bar data={bloodPressureData} options={bloodPressureOptions} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <span className="text-gray-500 text-sm">No data available</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blood Oxygen Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5 overflow-hidden">
        <div className="flex items-center justify-between w-full">
          {/* Left Panel - Icon and Current Value */}
          <div className="flex flex-col items-start mb-20">
            <div className="flex items-center gap-2 mb-3">
              <img src="/images/blood_oxygen.png" alt="Blood Oxygen" className="h-6 w-6" />
              <span className="text-lg font-bold text-gray-800">Blood Oxygen</span>
            </div>
            {hasBloodOxygenData && (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className={`text-3xl font-bold ${getBloodOxygenColor(bloodOxygen)}`}>{bloodOxygen}%</span>
                  {bloodOxygen < 90 && (
                    <svg className="h-5 w-5 text-rag-red" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                    </svg>
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {latestBloodOxygenTime}
                </div>
              </div>
            )}
          </div>
          
          {/* Right Panel - Bar Chart */}
          <div className="flex-1 h-48 ml-6">
            {hasBloodOxygenData ? (
              <Bar data={bloodOxygenData} options={bloodOxygenOptions} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <span className="text-gray-500 text-sm">No data available</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 